import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * ScrollToTop - Intelligentes Scroll-Management bei Routen-Wechsel
 *
 * Features:
 * - Speichert Scroll-Position in sessionStorage bei jedem Scroll (debounced)
 * - Stellt Scroll-Position bei "Back"-Navigation wieder her
 * - Scrollt nur bei "Forward"-Navigation nach oben
 */

const SCROLL_POSITIONS_KEY = 'scroll-positions';
const MAX_STORED_POSITIONS = 50;
const SCROLL_SAVE_DEBOUNCE = 150; // ms

const SCROLL_CONTAINER_SELECTORS = [
  'main.app-main',
  'main.app-layout-main',
  '.app-layout-main',
  '[data-scroll-container]',
];

function getScrollPositions(): Record<string, number> {
  try {
    const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveScrollPositions(positions: Record<string, number>) {
  try {
    const keys = Object.keys(positions);
    if (keys.length > MAX_STORED_POSITIONS) {
      const toRemove = keys.slice(0, keys.length - MAX_STORED_POSITIONS);
      toRemove.forEach((key) => delete positions[key]);
    }
    sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
  } catch {
    // Ignore storage errors
  }
}

function getCurrentScrollPosition(): number {
  for (const selector of SCROLL_CONTAINER_SELECTORS) {
    const element = document.querySelector(selector);
    if (element && element.scrollTop > 0) {
      return element.scrollTop;
    }
  }
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function scrollAllContainersTo(position: number) {
  window.scrollTo(0, position);
  document.documentElement.scrollTop = position;
  document.body.scrollTop = position;

  const selectors = [
    ...SCROLL_CONTAINER_SELECTORS,
    '.admin-layout-root',
    '.app-wrapper',
  ];

  selectors.forEach((selector) => {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      element.scrollTop = position;
    });
  });
}

export function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  const currentKeyRef = useRef<string>(key);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNavigatingRef = useRef<boolean>(false);
  const listenersAttachedRef = useRef<boolean>(false);

  // Update current key ref when it changes
  useEffect(() => {
    currentKeyRef.current = key;
  }, [key]);

  // Debounced save function
  const saveCurrentScrollPosition = useCallback(() => {
    if (isNavigatingRef.current) return;
    const currentKey = currentKeyRef.current;
    if (!currentKey) return;

    const scrollPos = getCurrentScrollPosition();
    const positions = getScrollPositions();
    positions[currentKey] = scrollPos;
    saveScrollPositions(positions);
  }, []);

  // Set up scroll listeners on scroll containers
  useEffect(() => {
    const handleScroll = () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(saveCurrentScrollPosition, SCROLL_SAVE_DEBOUNCE);
    };

    const handleBeforeUnload = () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveCurrentScrollPosition();
    };

    // Function to attach scroll listeners to all scroll containers
    const attachListeners = () => {
      const containers: Element[] = [];
      SCROLL_CONTAINER_SELECTORS.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => containers.push(el));
      });

      containers.forEach((el) => {
        el.addEventListener('scroll', handleScroll, { passive: true });
      });
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('beforeunload', handleBeforeUnload);

      return containers;
    };

    // Attach listeners
    const containers = attachListeners();
    listenersAttachedRef.current = true;

    // Use MutationObserver to detect when new containers appear
    const observer = new MutationObserver(() => {
      // Re-check for new scroll containers
      const newContainers: Element[] = [];
      SCROLL_CONTAINER_SELECTORS.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => {
          if (!containers.includes(el)) {
            newContainers.push(el);
            containers.push(el);
          }
        });
      });

      newContainers.forEach((el) => {
        el.addEventListener('scroll', handleScroll, { passive: true });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      containers.forEach((el) => {
        el.removeEventListener('scroll', handleScroll);
      });
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      observer.disconnect();
      listenersAttachedRef.current = false;
    };
  }, [saveCurrentScrollPosition]);

  // Handle scroll on route change
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual';
      } catch {
        // ignore
      }
    }

    // Mark that we're navigating
    isNavigatingRef.current = true;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    const handleScroll = () => {
      if (navigationType === 'POP') {
        const positions = getScrollPositions();
        const savedPosition = positions[key];

        if (savedPosition !== undefined && savedPosition > 0) {
          // Use multiple attempts to ensure scroll restoration works
          const attemptScroll = (attempts: number) => {
            if (attempts <= 0) {
              isNavigatingRef.current = false;
              return;
            }

            requestAnimationFrame(() => {
              scrollAllContainersTo(savedPosition);

              // Verify scroll worked, retry if not
              const currentPos = getCurrentScrollPosition();
              if (Math.abs(currentPos - savedPosition) > 50 && attempts > 1) {
                setTimeout(() => attemptScroll(attempts - 1), 50);
              } else {
                setTimeout(() => {
                  isNavigatingRef.current = false;
                }, 100);
              }
            });
          };

          attemptScroll(3);
          return;
        }
      }

      // PUSH or REPLACE navigation - scroll to top
      scrollAllContainersTo(0);
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    };

    handleScroll();
    queueMicrotask(handleScroll);
  }, [pathname, key, navigationType]);

  return null;
}
